import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/UserOrders.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaMapMarkedAlt, FaPhoneAlt, FaSyncAlt } from "react-icons/fa";

/**
 * SearchOrder - delivery person page to search orders and update delivery status
 *
 * - Search: GET /api/user/admin/showorder?orderId=<orderId>
 * - Update status: POST /api/user/admin/order/deliveryStatus/update  (body: { orderId, deliveryStatus, deliveryTime, deliveryDate })
 *
 * Note: keep your backend endpoints same, only improved front-end UX and bug fixes.
 */

const SearchOrder = () => {
  const navigate = useNavigate();

  const [allOrderData, setAllOrderData] = useState([]); // flat results from backend
  const [groupedOrders, setGroupedOrders] = useState([]); // grouped by orderId for cards (optional)
  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const deliveryStatus = ["Delivered", "On the Way", "Processing"];
  const deliveryTime = ["Morning", "Afternoon", "Evening", "Night"];

  const [orderDeliveryStatus, setOrderDeliveryStatus] = useState({
    orderId: "",
    deliveryStatus: "",
    deliveryTime: "",
    deliveryDate: "",
  });

  // fetch orders by orderId (or empty)
  const retrieveAllOrder = async (queryOrderId = "") => {
    try {
      const resp = await axios.get(
        `https://goldenhides.in/api/user/admin/showorder${
          queryOrderId ? `?orderId=${encodeURIComponent(queryOrderId)}` : ""
        }`
      );
      return resp.data;
    } catch (err) {
      console.error("Failed to retrieve orders", err);
      toast.error("Failed to fetch orders from server.");
      return null;
    }
  };

  // handle search submit
  const searchOrderById = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    const data = await retrieveAllOrder(orderId.trim());
    setLoading(false);
    if (data) {
      const orders = data.orders ?? data;
      const arr = Array.isArray(orders) ? orders : [];
      setAllOrderData(arr);
      groupOrders(arr);
    } else {
      setAllOrderData([]);
      setGroupedOrders([]);
    }
    setOrderId("");
  };

  // group flat rows into orders (by orderId)
  const groupOrders = (flat) => {
    if (!flat || flat.length === 0) {
      setGroupedOrders([]);
      return;
    }
    const map = new Map();
    flat.forEach((o) => {
      const id = o.orderId ?? `order_${Math.random().toString(36).slice(2, 9)}`;
      if (!map.has(id)) {
        map.set(id, {
          orderId: id,
          orderDate: o.orderDate,
          deliveryDate: o.deliveryDate,
          deliveryStatus: o.deliveryStatus,
          userName: o.userName,
          userPhone: o.userPhone,
          deliveryAddress: o.deliveryAddress ?? {},
          items: [],
        });
      }
      map.get(id).items.push({
        productId: o.productId,
        productName: o.productName,
        productDescription: o.productDescription,
        productImage: o.productImage,
        quantity: o.quantity,
        size: o.size,
        totalPrice: o.totalPrice,
      });
    });
    setGroupedOrders(Array.from(map.values()));
  };

  // handle form field changes (top update form)
  const handleOrderDelivery = (e) => {
    const { name, value } = e.target;
    setOrderDeliveryStatus((prev) => ({ ...prev, [name]: value }));
  };

  // update delivery status (POST)
  const updateDeliveryStatus = async (e) => {
    if (e) e.preventDefault();
    if (!orderDeliveryStatus.orderId) {
      toast.error("Please enter/select Order Id to update.");
      return;
    }
    setUpdating(true);
    try {
      const resp = await axios.post(
        "https://goldenhides.in/api/user/admin/order/deliveryStatus/update",
        orderDeliveryStatus,
        { headers: { "Content-Type": "application/json" } }
      );
      const res = resp.data;
      if (res?.success) {
        toast.success(res.responseMessage || "Delivery status updated.", {
          position: "top-center",
          autoClose: 1200,
        });

        // If server returns updated orders list, use it; otherwise refresh current search
        if (res.orders && Array.isArray(res.orders)) {
          setAllOrderData(res.orders);
          groupOrders(res.orders);
        } else {
          // re-run last search if any (safe fallback)
          const data = await retrieveAllOrder();
          const orders = data?.orders ?? data;
          const arr = Array.isArray(orders) ? orders : [];
          setAllOrderData(arr);
          groupOrders(arr);
        }

        // reset update form
        setOrderDeliveryStatus({
          orderId: "",
          deliveryStatus: "",
          deliveryTime: "",
          deliveryDate: "",
        });
      } else {
        toast.error(res.responseMessage || "Failed to update status.");
      }
    } catch (err) {
      console.error("Update delivery status error", err);
      toast.error("Server error while updating delivery status.");
    } finally {
      setUpdating(false);
    }
  };

  const formatDateFromEpoch = (epochTime) => {
    if (!epochTime) return "-";
    const d = new Date(Number(epochTime));
    const opts = { day: "2-digit", month: "short", year: "numeric" };
    const datePart = d.toLocaleDateString(undefined, opts);
    const timePart = d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart}, ${timePart}`;
  };

  const viewDirection = (latitude, longitude) => {
    if (!latitude || !longitude) {
      toast.error("Delivery coordinates not available for this address.");
      return;
    }
    navigate(`/delivery/route/${latitude}/${longitude}`);
  };

  // quick-load an order into the top update form
  const loadOrderToForm = (order) => {
    setOrderDeliveryStatus((prev) => ({
      ...prev,
      orderId: order.orderId || "",
      // keep other fields as-is
    }));
    // scroll to top so user sees the form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // initial: (optional) do not auto-load everything on mount — keep search-driven
  useEffect(() => {
    // keep empty
  }, []);

  return (
    <div className="mt-3 px-2">
      <ToastContainer />
      <div className="container-fluid">
        <div className="text-center mb-3">
          <h2 className="fw-bold text-success">Search & Update Orders</h2>
          <p className="text-muted mb-0">
            Search by Order ID and update delivery status
          </p>
        </div>

        {/* Top: Search */}
        <div className="card form-card ms-2 me-2 mb-4 custom-bg">
          <div className="card-header text-center bg-color custom-bg-text">
            <h4>Search Customer Orders</h4>
          </div>
          <div className="card-body" style={{ overflowY: "auto" }}>
            <form className="row g-3" onSubmit={searchOrderById}>
              <div className="col-auto">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Order Id..."
                  onChange={(e) => setOrderId(e.target.value)}
                  value={orderId}
                />
              </div>
              <div className="col-auto">
                <button
                  type="submit"
                  className="btn bg-color custom-bg-text mb-3"
                >
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary ms-2"
                  onClick={() => {
                    setOrderId("");
                    setAllOrderData([]);
                    setGroupedOrders([]);
                  }}
                >
                  Clear
                </button>
              </div>
            </form>

            {loading ? (
              <div className="text-center mt-3">Loading orders...</div>
            ) : allOrderData.length === 0 ? (
              <div className="text-center mt-3 text-muted">
                No orders. Use Search to lookup an order.
              </div>
            ) : null}
          </div>
        </div>

        {/* Update form */}
        <div className="card form-card ms-2 me-2 mb-4 custom-bg">
          <div className="card-header text-center bg-color custom-bg-text">
            <h4>Update Delivery Status</h4>
          </div>
          <div className="card-body">
            <form className="row g-3" onSubmit={updateDeliveryStatus}>
              <div className="col-md-3">
                <label className="text-color">
                  <b>Order Id</b>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Order Id..."
                  name="orderId"
                  onChange={handleOrderDelivery}
                  value={orderDeliveryStatus.orderId}
                />
              </div>

              <div className="col-md-3">
                <label className="text-color">
                  <b>Select Delivery Date</b>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="deliveryDate"
                  placeholder="dd-mm-yyyy"
                  min="1997-01-01"
                  max="2030-12-31"
                  value={orderDeliveryStatus.deliveryDate}
                  onChange={handleOrderDelivery}
                />
              </div>

              <div className="col-md-3">
                <label className="text-color">
                  <b>Delivery Time</b>
                </label>
                <select
                  name="deliveryTime"
                  value={orderDeliveryStatus.deliveryTime}
                  onChange={handleOrderDelivery}
                  className="form-control"
                >
                  <option value="">Select Delivery Time</option>
                  {deliveryTime.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-2">
                <label className="text-color">
                  <b>Delivery Status</b>
                </label>
                <select
                  name="deliveryStatus"
                  value={orderDeliveryStatus.deliveryStatus}
                  onChange={handleOrderDelivery}
                  className="form-control"
                >
                  <option value="">Select Delivery Status</option>
                  {deliveryStatus.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-1 d-flex align-items-end">
                <button
                  type="submit"
                  className="btn bg-color custom-bg-text w-100"
                  disabled={updating}
                >
                  {updating ? (
                    <>
                      <FaSyncAlt className="me-1 spin" /> Updating...
                    </>
                  ) : (
                    "Update"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Optional: grouped card view for more details */}
        {groupedOrders.length > 0 && (
          <div>
            {groupedOrders.map((order) => {
              const orderTotal = order.items.reduce(
                (acc, it) => acc + Number(it.totalPrice || 0),
                0
              );
              const address = order.deliveryAddress ?? {};
              const isDelivered = order.deliveryStatus === "Delivered";

              return (
                <div
                  key={order.orderId}
                  className="card form-card ms-2 me-2 mb-4 custom-bg orders-group-card"
                >
                  <div className="card-header d-flex align-items-center justify-content-between bg-white">
                    <div className="d-flex align-items-center gap-4">
                      <div>
                        <div className="small text-muted">Order ID</div>
                        <div style={{ fontWeight: 700 }}>#{order.orderId}</div>
                      </div>

                      <div>
                        <div className="small text-muted">Items</div>
                        <div style={{ fontWeight: 600 }}>
                          {order.items.length} item
                          {order.items.length > 1 ? "s" : ""}
                        </div>
                      </div>

                      <div>
                        <div className="small text-muted">Ordered</div>
                        <div style={{ fontWeight: 600 }}>
                          {formatDateFromEpoch(order.orderDate)}
                        </div>
                      </div>

                      <div>
                        <div className="small text-muted">Customer</div>
                        <div style={{ fontWeight: 600 }}>
                          {order.userName || "-"}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-3">
                      <div className="text-end small">
                        <div className="small text-muted">Contact</div>
                        <div style={{ fontWeight: 600 }}>
                          {order.userPhone || "-"}
                        </div>
                      </div>

                      <div>
                        <span
                          className={`badge order-status-badge ${
                            (order.deliveryStatus || "").toLowerCase() ===
                            "processing"
                              ? "badge-pending"
                              : "badge-other"
                          }`}
                        >
                          {order.deliveryStatus || "Pending"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="row g-3">
                      {order.items.map((item, idx) => (
                        <div
                          key={`${order.orderId}_item_${idx}`}
                          className="col-12 col-md-4"
                        >
                          <div className="order-product-card p-3 h-100 shadow-sm rounded">
                            <div className="d-flex gap-3">
                              <div style={{ width: 90, height: 90 }}>
                                {item.productImage ? (
                                  <img
                                    src={`https://goldenhides.in/api/product/${item.productImage}`}
                                    alt={item.productName}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: 6,
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      background: "#f1f1f1",
                                      borderRadius: 6,
                                    }}
                                  />
                                )}
                              </div>

                              <div className="flex-grow-1">
                                <div style={{ fontWeight: 700 }}>
                                  {item.productName}
                                </div>
                                <div className="small text-muted mt-1">
                                  {item.productDescription
                                    ? item.productDescription.slice(0, 80) +
                                      (item.productDescription.length > 80
                                        ? "..."
                                        : "")
                                    : ""}
                                </div>

                                <div className="mt-2 small text-muted">
                                  Qty: {item.quantity} &nbsp;|&nbsp; Size:{" "}
                                  {item.size || "-"}
                                </div>
                              </div>

                              <div style={{ textAlign: "right", minWidth: 90 }}>
                                <div
                                  className="text-success"
                                  style={{ fontWeight: 700 }}
                                >
                                  ₹ {Number(item.totalPrice || 0).toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="card-footer bg-white d-flex justify-content-between align-items-center">
                    <div className="small text-muted">
                      <div>Delivery Address</div>
                      <div style={{ fontWeight: 600 }}>
                        {address.addressLine ?? "-"}, {address.city ?? "-"}
                        {address.downtown ? `, ${address.downtown}` : ""}
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      {!isDelivered && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() =>
                            viewDirection(address.latitude, address.longitude)
                          }
                        >
                          <FaMapMarkedAlt className="me-1" /> Direction
                        </button>
                      )}

                      <a
                        href={`tel:${order.userPhone ?? ""}`}
                        className="btn btn-sm btn-outline-success"
                      >
                        <FaPhoneAlt className="me-1" /> Call
                      </a>

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => loadOrderToForm(order)}
                      >
                        Load to form
                      </button>

                      <div
                        style={{
                          fontWeight: 700,
                          color: "#0a7a3b",
                          fontSize: 18,
                        }}
                      >
                        Total ₹ {orderTotal.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchOrder;
